import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { MsalProvider } from '@azure/msal-react';
import { broadcastResponseToMainFrame } from '@azure/msal-browser/redirect-bridge';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './index.css';
// App.jsx is preserved as-is during the .tsx migration. Migrate in v1.1.
// With tsconfig.app.json#compilerOptions.allowJs = true, no @ts-expect-error needed.
import App from './App.jsx';
import { msalInstance } from './config/auth';
import { isMsalChildWindow } from './config/authSignals';
import AuthGate from './components/AuthGate';

function bootstrap(): void {
  // MSAL's hidden renewal iframe — and its sign-in popup — land HERE, because the
  // redirect URI is this origin. In msal-browser v5 the window that asked waits on
  // a BroadcastChannel for this page to pass the answer back, and waits in vain if
  // the app renders instead; the rendered copy then asks for tokens of its own.
  // So: hand the answer back, render nothing, and stop BEFORE MsalProvider mounts,
  // which would initialise MSAL and run handleRedirectPromise in this window.
  // config/authSignals.ts has the full account.
  if (isMsalChildWindow(window)) {
    broadcastResponseToMainFrame().catch((err: unknown) => {
      // Nobody can see this page. The window that asked times out and falls back
      // (acquireApiToken redirects to sign in), which is the recovery either way.
      console.error('[auth] could not hand the sign-in response back to the app', err);
    });
    return;
  }

  // Apply saved theme before first render to avoid flash.
  const savedTheme = localStorage.getItem('theme') || 'dark';
  document.documentElement.setAttribute('data-theme', savedTheme);

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
      },
    },
  });

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <AuthGate>
            <App />
          </AuthGate>
        </QueryClientProvider>
      </MsalProvider>
    </StrictMode>,
  );
}

bootstrap();
